import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Search, DollarSign, Phone, Mail, User, Calendar, TrendingUp,
  Wrench, Clock, MapPin, ExternalLink, Building2, Users,
  Briefcase, Truck, CheckCircle, AlertCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { filterJobsByPermissions } from '../utils/permissions';
import { cn } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sales Pipeline Stages
const SALES_STAGES = [
  { id: 'lead', name: 'Lead', color: 'bg-slate-100 dark:bg-slate-800', textColor: 'text-slate-700 dark:text-slate-300', icon: Users },
  { id: 'qualified', name: 'Qualified', color: 'bg-blue-50 dark:bg-blue-900/50', textColor: 'text-blue-700 dark:text-blue-300', icon: CheckCircle },
  { id: 'proposal', name: 'Proposal', color: 'bg-purple-50 dark:bg-purple-900/50', textColor: 'text-purple-700 dark:text-purple-300', icon: Briefcase },
  { id: 'negotiation', name: 'Negotiation', color: 'bg-orange-50 dark:bg-orange-900/50', textColor: 'text-orange-700 dark:text-orange-300', icon: TrendingUp },
  { id: 'closed_won', name: 'Closed Won', color: 'bg-green-50 dark:bg-green-900/50', textColor: 'text-green-700 dark:text-green-300', icon: CheckCircle },
  { id: 'closed_lost', name: 'Closed Lost', color: 'bg-red-50 dark:bg-red-900/50', textColor: 'text-red-700 dark:text-red-300', icon: AlertCircle }
];

// Operations Stages
const OPS_STAGES = [
  { id: 'scheduled', name: 'Scheduled', color: 'bg-blue-50 dark:bg-blue-900/50', textColor: 'text-blue-700 dark:text-blue-300', icon: Calendar },
  { id: 'prep', name: 'Prep', color: 'bg-purple-50 dark:bg-purple-900/50', textColor: 'text-purple-700 dark:text-purple-300', icon: Wrench },
  { id: 'production', name: 'Production', color: 'bg-orange-50 dark:bg-orange-900/50', textColor: 'text-orange-700 dark:text-orange-300', icon: Building2 },
  { id: 'installation', name: 'Installation', color: 'bg-yellow-50 dark:bg-yellow-900/50', textColor: 'text-yellow-700 dark:text-yellow-300', icon: Truck },
  { id: 'final_inspection', name: 'Final Inspection', color: 'bg-teal-50 dark:bg-teal-900/50', textColor: 'text-teal-700 dark:text-teal-300', icon: CheckCircle },
  { id: 'complete', name: 'Complete', color: 'bg-green-50 dark:bg-green-900/50', textColor: 'text-green-700 dark:text-green-300', icon: CheckCircle }
];

// Get all stage IDs for each department
const SALES_STAGE_IDS = SALES_STAGES.map(s => s.id);
const OPS_STAGE_IDS = OPS_STAGES.map(s => s.id);

export const UnifiedBoard = () => {
  const { user, viewAsRole, viewAsUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Determine initial view from URL or user's department
  const getInitialView = () => {
    const urlView = searchParams.get('view');
    if (urlView === 'sales' || urlView === 'operations') return urlView;
    // Default based on user role/department
    if (user?.role?.includes('sales') || user?.department === 'sales') return 'sales';
    if (user?.role?.includes('operations') || user?.department === 'operations') return 'operations';
    return 'sales'; // Default to sales
  };

  const [activeView, setActiveView] = useState(getInitialView());
  const [jobs, setJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRep, setFilterRep] = useState('all');
  const [draggedCard, setDraggedCard] = useState(null);

  const stages = activeView === 'sales' ? SALES_STAGES : OPS_STAGES;
  const stageIds = activeView === 'sales' ? SALES_STAGE_IDS : OPS_STAGE_IDS;

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [allJobs, activeView, user, viewAsRole, viewAsUser, users, searchTerm, filterRep, stageIds]);

  // Update URL when view changes
  useEffect(() => {
    setSearchParams({ view: activeView });
  }, [activeView, setSearchParams]);

  const fetchData = async () => {
    try {
      const [jobsRes, usersRes] = await Promise.all([
        axios.get(`${API}/jobs`),
        axios.get(`${API}/users`)
      ]);
      
      setAllJobs(jobsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load board data');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    if (!allJobs || allJobs.length === 0) {
      setJobs([]);
      return;
    }

    // Apply role-based filtering
    let effectiveUser = user;
    if (viewAsUser && viewAsRole) {
      const testUser = users.find(u => u.id === viewAsUser);
      if (testUser) effectiveUser = testUser;
    }

    let filtered = filterJobsByPermissions(allJobs, effectiveUser, users);

    // Filter by department stages
    filtered = filtered.filter(job => stageIds.includes(job.stage));

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(job => 
        job.customer_name?.toLowerCase().includes(term) ||
        job.customer_phone?.includes(term) ||
        job.job_type?.toLowerCase().includes(term) ||
        job.address?.toLowerCase().includes(term) ||
        job.job_number?.toLowerCase().includes(term)
      );
    }

    // Apply rep filter
    if (filterRep !== 'all') {
      filtered = filtered.filter(job => job.assigned_to === filterRep);
    }

    setJobs(filtered);
  };

  const handleDragStart = (e, job) => {
    setDraggedCard(job);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    
    if (!draggedCard || draggedCard.stage === targetStage) {
      setDraggedCard(null);
      return;
    }

    try {
      await axios.patch(`${API}/jobs/${draggedCard.id}`, {
        stage: targetStage
      });
      
      const stageName = stages.find(s => s.id === targetStage)?.name || targetStage;
      toast.success(`Moved to ${stageName}`);
      
      // Update local state for immediate feedback
      setAllJobs(prev => prev.map(job => 
        job.id === draggedCard.id ? { ...job, stage: targetStage } : job
      ));
    } catch (error) {
      console.error('Failed to update stage:', error);
      toast.error('Failed to move job');
    } finally {
      setDraggedCard(null);
    }
  };

  const getJobsByStage = (stageId) => {
    return jobs.filter(job => job.stage === stageId);
  };

  const formatCurrency = (value) => {
    if (!value) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getRepName = (repId) => {
    if (!repId) return 'Unassigned';
    const rep = users.find(u => u.id === repId);
    return rep ? `${rep.first_name} ${rep.last_name}` : 'Unknown';
  };

  const handleViewChange = (newView) => {
    setActiveView(newView);
    setSearchTerm('');
    setFilterRep('all');
  };

  const handleJobClick = (job) => {
    navigate(`/jobs/${job.id}`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading board...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const totalValue = jobs.reduce((sum, job) => sum + (job.estimated_value || 0), 0);

  return (
    <Layout>
      <div className="h-[calc(100vh-64px)] flex flex-col bg-background">
        {/* Header */}
        <div className="bg-card border-b px-6 py-4 space-y-4">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold">
                {activeView === 'sales' ? 'Sales Pipeline' : 'Operations Board'}
              </h1>
              <p className="text-muted-foreground text-sm">
                {activeView === 'sales' 
                  ? 'Manage sales opportunities and track deals' 
                  : 'Track production and installation workflow'}
              </p>
            </div>
            
            {/* View Toggle */}
            <Tabs value={activeView} onValueChange={handleViewChange}>
              <TabsList className="grid grid-cols-2 w-64">
                <TabsTrigger value="sales" className="gap-2">
                  <DollarSign className="h-4 w-4" />
                  Sales
                </TabsTrigger>
                <TabsTrigger value="operations" className="gap-2">
                  <Wrench className="h-4 w-4" />
                  Operations
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Stats Bar */}
          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{jobs.length}</span>
              <span className="text-muted-foreground">Jobs</span>
            </div>
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-600" />
              <span className="font-medium text-green-600">{formatCurrency(totalValue)}</span>
              <span className="text-muted-foreground">Total Value</span>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-4">
            <div className="flex-1 relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder={activeView === 'sales' ? 'Search leads...' : 'Search jobs...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterRep} onValueChange={setFilterRep}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Team Members" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Team Members</SelectItem>
                {users.map(u => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.first_name} {u.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Board Columns */}
        <div className="flex-1 overflow-x-auto p-4">
          <div className="flex gap-4 h-full min-w-max">
            {stages.map(stage => {
              const stageJobs = getJobsByStage(stage.id);
              const stageValue = stageJobs.reduce((sum, job) => sum + (job.estimated_value || 0), 0);
              const StageIcon = stage.icon;

              return (
                <div
                  key={stage.id}
                  className="flex-shrink-0 w-80 flex flex-col"
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, stage.id)}
                >
                  {/* Column Header */}
                  <div className={cn("p-4 rounded-t-lg", stage.color)}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <StageIcon className={cn("h-4 w-4", stage.textColor)} />
                        <h3 className={cn("font-semibold", stage.textColor)}>{stage.name}</h3>
                      </div>
                      <Badge variant="secondary" className="bg-white dark:bg-gray-800">
                        {stageJobs.length}
                      </Badge>
                    </div>
                    {stageValue > 0 && (
                      <p className={cn("text-sm mt-1 opacity-80", stage.textColor)}>
                        {formatCurrency(stageValue)}
                      </p>
                    )}
                  </div>

                  {/* Cards Container */}
                  <div className="flex-1 bg-muted/50 p-3 space-y-3 overflow-y-auto rounded-b-lg min-h-[200px]">
                    {stageJobs.length === 0 ? (
                      <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
                        <p>No {activeView === 'sales' ? 'leads' : 'jobs'}</p>
                      </div>
                    ) : (
                      stageJobs.map(job => (
                        <Card
                          key={job.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, job)}
                          onClick={() => handleJobClick(job)}
                          className={cn(
                            "cursor-pointer transition-all hover:shadow-md hover:scale-[1.02]",
                            draggedCard?.id === job.id && "opacity-50"
                          )}
                        >
                          <CardContent className="p-4 space-y-3">
                            {/* Header Row */}
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold truncate">
                                  {job.customer_name}
                                </h4>
                                {job.job_number && (
                                  <p className="text-xs text-muted-foreground">
                                    #{job.job_number}
                                  </p>
                                )}
                              </div>
                              {job.priority === 'urgent' && (
                                <Badge variant="destructive" className="ml-2 text-xs">Urgent</Badge>
                              )}
                              {job.priority === 'high' && (
                                <Badge className="ml-2 bg-orange-500 text-xs">High</Badge>
                              )}
                            </div>

                            {/* Job Type */}
                            {job.job_type && (
                              <p className="text-sm text-muted-foreground truncate">
                                {job.job_type}
                              </p>
                            )}

                            {/* Value */}
                            {job.estimated_value && (
                              <div className="flex items-center gap-2 text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-3 py-2 rounded">
                                <DollarSign className="h-4 w-4" />
                                <span className="font-semibold">{formatCurrency(job.estimated_value)}</span>
                              </div>
                            )}

                            {/* Contact/Location Info */}
                            <div className="space-y-1 text-xs text-muted-foreground">
                              {activeView === 'sales' && job.customer_phone && (
                                <div className="flex items-center gap-2">
                                  <Phone className="h-3 w-3" />
                                  <span>{job.customer_phone}</span>
                                </div>
                              )}
                              {activeView === 'operations' && job.address && (
                                <div className="flex items-center gap-2">
                                  <MapPin className="h-3 w-3" />
                                  <span className="truncate">{job.address}</span>
                                </div>
                              )}
                              {job.scheduled_date && (
                                <div className="flex items-center gap-2">
                                  <Calendar className="h-3 w-3" />
                                  <span>{new Date(job.scheduled_date).toLocaleDateString()}</span>
                                </div>
                              )}
                            </div>

                            {/* Footer: Assigned To */}
                            <div className="flex items-center justify-between pt-2 border-t">
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <User className="h-3 w-3" />
                                <span>{getRepName(job.assigned_to)}</span>
                              </div>
                              <ExternalLink className="h-3 w-3 text-muted-foreground" />
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UnifiedBoard;
